<template>
  <div class="file-uploader upload-excel">
    <input
      ref="excel-upload-input"
      class="excel-upload-input"
      type="file"
      :accept="uploadExcelAccepts"
      @change="handleClick"
      style="display: none; z-index: -9999"
    />
    <div
      v-show="!loading"
      class="drop upload-zone py-3"
      @drop="handleDrop"
      @dragover="handleDragover"
      @dragenter="handleDragover"
      style="width: 480px; max-width: 100%; margin: 0 auto"
    >
      <div class="upload-message d-flex flex-column gap-1">
        <div class="d-flex flex-column gap-0 justify-content-center align-items-center">
          <ficon
            type="cloud-upload-alt"
            style="font-size: 1.5rem"
            class="mx-auto"
          />
          Excel, CSVファイルをドロップ
        </div>
        <div class="small text-muted">または</div>
        <div class="d-flex flex-row gap-2 justify-content-center align-items-center">
          <span
            @click.prevent.stop="() => handleUpload()"
            class="btn btn-outline-secondary btn-sm"
          >
            <ficon type="plus" /> ファイルを選択
          </span>
        </div>
      </div>
    </div>
    <div
      v-show="loading"
      class="_text-center py-4"
    >
      <div class="mb-2 d-flex flex-row gap-2 align-items-center justify-content-center">
        <ficon type="file-upload" />
        <code> {{ uploadingFileName }}</code>
        <span
          class="btn btn-outline-danger btn-sm small _ml-1 _p-1"
          style="padding: 1px 2px; font-size: 9px"
          @click.prevent="() => resetState()"
          ><ficon type="x"
        /></span>
      </div>
      <div
        v-if="letSelectSheetName && sheetNameSelection.length"
        class=""
      >
        <div class="card">
          <div class="card-header bg-white d-flex flex-row gap-2 align-items-center">
            読み込み対象シートを選択
            <div
              v-if="allowMultipleSheetSelection"
              class="small"
            >
              <a
                href="#"
                @click.prevent="
                  () => {
                    selectedSheetNames = sheetNameSelection
                  }
                "
                >すべてのシートを選択</a
              >
              /
              <a
                href="#"
                @click.prevent="() => (selectedSheetNames = [])"
                >選択解除</a
              >
            </div>
          </div>

          <div class="card-body py-2">
            <component
              :is="
                allowMultipleSheetSelection
                  ? 'b-form-checkbox-group'
                  : 'b-form-radio-group'
              "
              :model-value="
                allowMultipleSheetSelection ? selectedSheetNames : selectedSheetNames[0]
              "
              @update:model-value="
                (value) => {
                  selectedSheetNames = typeof value === 'string' ? [value] : value
                }
              "
              class="_form-control-sm"
              :options="sheetNameSelection"
            />
          </div>
          <div
            class="card-footer bg-white d-flex gap-2 align-items-center justify-content-center"
          >
            <span
              class="btn"
              :class="
                !!selectedSheetNames.length ? `btn-primary` : `btn-outline-secondary`
              "
              :disabled="!!selectedSheetNames.length"
              v-single-click="() => selectSheetName()"
              >読み込み実行</span
            >
          </div>
        </div>
      </div>
      <Loading v-else>読み込み中...</Loading>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue'

const _emptyValueCells = ['', null, undefined]
const extendPreviousRowValuesForEmptyValueCells = (rows: Record<string, any>[]) => {
  return rows.map((row, index) => {
    if (index === 0) {
      return row
    }
    const previousRow = rows[index - 1]
    const extendedRow = { ...row }
    for (const key in row) {
      if (_emptyValueCells.includes(extendedRow[key])) {
        extendedRow[key] = previousRow[key]
      }
    }
    return {
      ...previousRow,
      ...extendedRow,
    }
  })
}

export default {
  props: {
    // sheetNameDetectFunction: {default: false}, // eslint-disable-line
    letSelectSheetName: { default: false, type: Boolean }, // eslint-disable-line
    beforeUpload: Function, // eslint-disable-line
    /**
     * Excelファイル 読み込み成功時のコールバック関数
     * @param {Object} params
     * @param {string[]} params.selectedSheetNames
     * @param {string} params.uploadingFileName
     * @param {string[]} params.header
     * @param {Object[]} params.results
     */
    onSuccess: Function as PropType<({
      header,
      results,
      selectedSheetNames,
      uploadingFileName,
    }: {
      header: string[]
      results: Record<string, any>[]
      selectedSheetNames: string[]
      uploadingFileName: string
    }) => any>, // eslint-disable-line
    uploadExcelAccepts: {
      default: '.xlsx,.xls,.csv',
      type: String,
    },
    xlsxSheetToJsonOptions: {
      default: null,
      type: Object,
    },
    xlsxParseOptionFunction: {
      default: null,
      type: Function as PropType<
        ({ workbook, sheetName, selectedSheetNames }) => Promise<Record<string, any>>
      >,
    },
    xlsxParseOptionFunctionAsString: {
      default: null,
      type: String,
    },
    allowMultipleSheetSelection: {
      default: false,
      type: Boolean,
    },
    shouldExtendPreviousRowValuesForEmptyValueCells: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      loading: false,
      excelData: {
        header: null,
        results: null,
      },
      sheetNameSelection: [],
      selectedSheetNames: [],
      uploadingFileName: '',
      selectCallbackFunction: null as any,
    }
  },
  async created() {
    // await $core.$exportAsExcel.initLib()
  },
  methods: {
    async generateData({ header, results }) {
      if (this.onSuccess) {
        return this.onSuccess({
          header: header || [],
          results: results || [],
          selectedSheetNames: this.selectedSheetNames,
          uploadingFileName: this.uploadingFileName,
        })
      }
      return
    },
    handleDrop(e) {
      e.stopPropagation()
      e.preventDefault()
      if (this.loading) return
      const files = e.dataTransfer.files
      if (files.length !== 1) {
        $core.$toast.errorToast('1ファイルのみ選択してください')
        return
      }
      const rawFile = files[0] // only use files[0]

      if (!this.isExcel(rawFile)) {
        $core.$toast.errorToast('拡張子 .xlsx, .xls のファイルのみ利用可能です')
        return false
      }
      this.upload(rawFile)
      e.stopPropagation()
      e.preventDefault()
    },
    handleDragover(e) {
      e.stopPropagation()
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    },
    handleUpload() {
      // @ts-ignore
      this.$refs['excel-upload-input'].click()
    },
    handleClick(e) {
      const files = e.target.files
      const rawFile = files[0] // only use files[0]
      if (!rawFile) return
      this.upload(rawFile)
    },
    upload(rawFile) {
      // @ts-ignore
      this.$refs['excel-upload-input'].value = null // fix can't select the same excel
      this.sheetNameSelection = []

      let isAllowUpload = true
      if (this.beforeUpload) {
        isAllowUpload = this.beforeUpload(rawFile)
      } else {
        const isOverSizedFile = rawFile.size / 1024 / 1024 > 50
        if (isOverSizedFile) {
          $core.$toast.errorToast('Excelファイルサイズの上限は50MBです。')
          isAllowUpload = false
        }
      }
      if (isAllowUpload) {
        this.readerData(rawFile)
      }
    },
    async readerData(rawFile: File) {
      this.loading = true
      this.uploadingFileName = rawFile.name
      // CSV の場合には 自動的に UTF-8 に変換して Emit する
      if (rawFile.name.match(/\.csv$/i)) {
        const _conv =
          await $core.$utils.autoDetectEncodingAndConvertToUtf8WithFile(rawFile)
        const arrayOfObjects = $core.$utils.csvParserLite(_conv.text)
        await this.generateData({
          header: Object.keys(arrayOfObjects[0] || {}),
          results: arrayOfObjects,
        })
        this.$nextTick(() => {
          this.resetState()
          $core.$loading.finish()
        })
        return
      }
      return new Promise(async (resolve, reject) => {
        const reader = new FileReader()
        reader.onload = async (e) => {
          const data = e.target.result
          let workbook = null
          try {
            workbook = $core.$exportAsExcel.XLSX.read(data, {
              type: 'array',
              cellDates: true,
            })
          } catch (e) {
            console.error(e)
            globalThis.$core.$toast.errorToast(
              e.message === 'File is password-protected'
                ? 'アップロードするには、パスワード保護を解除して下さい。'
                : 'エラーが発生しました、ファイル内容を確認して下さい。',
            )
            this.resetState()
            return reject(e)
          }
          this.selectCallbackFunction = async (targetSheetNames) => {
            $core.$loading.start('読み込み中...', 'overlay')
            const readSheets = async () => {
              const results = []
              let readingSheetIndex = 0
              for (const sheetName of targetSheetNames) {
                const worksheet = workbook.Sheets[sheetName]
                const xlsxParseOption: any = {
                  ...(this.xlsxSheetToJsonOptions || {}),
                  ...((await this.parseOption(
                    workbook,
                    sheetName,
                    targetSheetNames,
                    readingSheetIndex,
                    results,
                  )) || {}),
                  blankrows: false,
                }
                console.log(
                  'CORE/src/front/ExcelUpload/UploadExcel.vue: xlsxParseOption',
                  xlsxParseOption,
                )
                // 特殊Case: xlsxParseOption.readResultData が 存在する場合は その値を 結果として返す
                let sheetResults = Array.isArray(xlsxParseOption.readResultData)
                  ? xlsxParseOption.readResultData
                  : $core.$exportAsExcel.sheetToJson(worksheet, xlsxParseOption)
                if (this.shouldExtendPreviousRowValuesForEmptyValueCells) {
                  sheetResults = extendPreviousRowValuesForEmptyValueCells(sheetResults)
                }
                results.push(sheetResults)
                readingSheetIndex++
              }
              return results.flat()
            }
            // const worksheet = workbook.Sheets[targetSheetNames[0]]
            const results = await readSheets()
            console.log('CORE/src/front/ExcelUpload/UploadExcel.vue: results', results)
            const header = Object.keys(results[0] || {})
            await this.generateData({ header, results })
            this.$nextTick(() => {
              this.resetState()
              $core.$loading.finish()
            })
            resolve(void 0)
          }
          if (this.letSelectSheetName) {
            this.sheetNameSelection = workbook.SheetNames
            this.selectedSheetNames = this.sheetNameSelection[0]
              ? [this.sheetNameSelection[0]]
              : []
          } else {
            const targetSheetNames = [workbook.SheetNames[0]]
            this.selectCallbackFunction(targetSheetNames)
          }
        }
        reader.readAsArrayBuffer(rawFile)
      })
    },
    async parseOption(
      workbook,
      sheetName: string,
      selectedSheetNames: string[],
      readingSheetIndex: number,
      readResults: Record<string, any>[], // すでに読み取った データ行の配列
    ): Promise<Record<string, any>> {
      const args = {
        workbook,
        sheetName,
        selectedSheetNames,
        readingSheetIndex,
        readResults,
      }
      if (typeof this.xlsxParseOptionFunction === 'function') {
        return this.xlsxParseOptionFunction(args)
      }
      if (typeof this.xlsxParseOptionFunctionAsString === 'string') {
        const executable = await $core.$utils.executeStringDefinedFunction({
          functionString: this.xlsxParseOptionFunctionAsString,
          functionArgValues: {},
        })
        return executable?.(args)
      }
      return {}
    },
    isExcel(file) {
      return /\.(xlsx|xls|csv)$/i.test(file.name)
    },
    selectSheetName() {
      if (!this.selectedSheetNames.length) {
        console.log(`シート名が不正です。`)
      } else {
        $core.$loading.start('読み込み中...', 'overlay')
        this.$nextTick(() => {
          this.selectCallbackFunction(this.selectedSheetNames)
        })
      }
    },
    resetState() {
      this.loading = false
      this.sheetNameSelection = []
      this.selectedSheetNames = []
      this.selectCallbackFunction = null
    },
  },
}
</script>
